import axios, { type AxiosError, type AxiosInstance } from 'axios';
import type { ConsentStatus, RecordConsentBody, SetMarketingConsentBody } from '../types/consent';
import type { Industry, SellerProfile, SellerStatus, SellerType } from '../types/leads';
import { API_URL, API_VERSION } from './apiUrl';
import { normalizeCountryCode } from './geo/localeCountry';
import { blockPath, isBlocked, isPlanError } from './plan-gate';
import { Sentry } from './sentry';

let getAccessToken: (() => Promise<string | null>) | null = null;
let forceTokenRefresh: (() => Promise<string | null>) | null = null;
let onAuthFailure: (() => void) | null = null;
let onOrgAccessDenied: (() => void) | null = null;

export const setAccessTokenGetter = (getter: () => Promise<string | null>) => {
  getAccessToken = getter;
};

export const setForceTokenRefresh = (refresher: () => Promise<string | null>) => {
  forceTokenRefresh = refresher;
};

export const setOnAuthFailure = (handler: () => void) => {
  onAuthFailure = handler;
};

export const setOnOrgAccessDenied = (handler: () => void) => {
  onOrgAccessDenied = handler;
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: any) => void;
}> = [];

function processQueue(token: string | null, error?: any) {
  failedQueue.forEach((pending) => {
    if (error) {
      pending.reject(error);
    } else {
      pending.resolve(token);
    }
  });
  failedQueue = [];
}

const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Serialize array params in repeat format (`tags=a&tags=b`, no brackets) so the
  // NestJS ValidationPipe sees a whitelisted `tags` key instead of an unknown
  // `tags[]` property (which `forbidNonWhitelisted` rejects with a 400).
  paramsSerializer: { indexes: null },
});

// Endpoints reachable without a JWT. Matching is ANCHORED (see
// `matchesPublicEndpoint`): a bare substring match would wrongly classify
// authenticated paths that merely contain one of these tokens — e.g. '/geo'
// substring-matched '/analytics/geo-distribution', stripping its Authorization
// header and 401-ing the call.
const PUBLIC_ENDPOINTS = [
  '/waitlist',
  '/access-gate',
  '/auth/e2e/login',
  '/blog',
  '/geo',
  '/pricing',
];

// True when the request path is exactly a public endpoint or a sub-path of one.
// The query string is stripped before comparison, and the endpoint token must
// be matched as a whole path segment (P === E || P starts with `${E}/`), so
// '/geo' matches '/geo' and '/geo?x=1' but never '/analytics/geo-distribution'.
function matchesPublicEndpoint(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const path = url.split('#')[0].split('?')[0];
  return PUBLIC_ENDPOINTS.some((endpoint) => path === endpoint || path.startsWith(`${endpoint}/`));
}

api.interceptors.request.use(
  async (config) => {
    const isPublicEndpoint = matchesPublicEndpoint(config.url);

    // Add Sentry breadcrumb for API request
    Sentry.addBreadcrumb({
      category: 'api.request',
      message: `${config.method?.toUpperCase()} ${config.url}`,
      level: 'info',
      data: {
        url: config.url,
        method: config.method,
        isPublic: isPublicEndpoint,
      },
    });

    if (!isPublicEndpoint && isBlocked(config.url || '')) {
      throw new axios.AxiosError(
        'Feature not available on your current plan',
        'ERR_PLAN_BLOCKED',
        config,
        null,
        {
          status: 403,
          statusText: 'Forbidden',
          data: {
            statusCode: 403,
            message: 'Feature blocked — upgrade your plan',
            error: 'Forbidden',
          },
          headers: {},
          config,
        } as any,
      );
    }

    if (getAccessToken && !isPublicEndpoint) {
      const token = await getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Store request start time for performance tracking
    (config as any).metadata = { startTime: Date.now() };

    return config;
  },
  (error) => {
    Sentry.captureException(error, {
      tags: { error_type: 'request_setup' },
    });
    throw error;
  },
);

function setAuthHeader(request: any, token: string) {
  request.headers = request.headers || {};
  request.headers.Authorization = `Bearer ${token}`;
}

async function handleTokenRefreshOnUnauthorized(
  error: AxiosError,
  originalRequest: any,
  _duration: number,
): Promise<any> {
  const statusCode = error.response?.status;
  if (statusCode !== 401 || originalRequest._retry || !forceTokenRefresh) return null;

  originalRequest._retry = true;

  if (isRefreshing) {
    return new Promise<string | null>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((token) => {
        if (token) setAuthHeader(originalRequest, token);
        return api(originalRequest);
      })
      .catch(() => {
        throw error;
      });
  }

  isRefreshing = true;

  try {
    const newToken = await forceTokenRefresh();

    if (newToken) {
      setAuthHeader(originalRequest, newToken);
      processQueue(newToken);
      return api(originalRequest);
    }

    processQueue(null);
    onAuthFailure?.();
    throw error;
  } catch (refreshError) {
    processQueue(null, refreshError);
    onAuthFailure?.();
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}

export function sanitizeAxiosErrorForCapture(error: AxiosError): AxiosError {
  const sanitized: AxiosError = Object.assign(Object.create(Object.getPrototypeOf(error)), error);

  if (sanitized.config) {
    sanitized.config = { ...sanitized.config, data: undefined, headers: undefined as any };
  }
  delete (sanitized as any).request;
  if (sanitized.response) {
    sanitized.response = { ...sanitized.response, data: undefined, request: undefined as any };
  }

  return sanitized;
}

function handleResponseError(error: AxiosError, originalRequest: any, duration: number) {
  const statusCode = error.response?.status ?? 0;
  const errorData = error.response?.data;
  const planGated = isPlanError(statusCode, errorData);

  if (statusCode >= 500) {
    console.error('API Error:', errorData);
  }

  if (planGated) {
    blockPath(originalRequest.url || '');
  }

  // A plan-gated 403 (org's plan doesn't include this feature) is an expected,
  // already-handled state — plan-gate.ts blocks the path client-side so
  // repeat requests don't even reach the network. Reporting it to Sentry on
  // every occurrence is noise, not a real error. Other 403s (e.g. org
  // membership checks) still get reported.
  if (!planGated && (statusCode >= 500 || statusCode === 403)) {
    Sentry.withScope((scope: any) => {
      scope.setContext('api_error', {
        url: error.config?.url,
        method: error.config?.method,
        statusCode,
        errorCode: (errorData as any)?.errorCode ?? (errorData as any)?.error_code,
        duration,
      });
      scope.setLevel(statusCode >= 500 ? 'error' : 'warning');
      scope.setTag('api_status', String(statusCode));
      Sentry.captureException(sanitizeAxiosErrorForCapture(error));
    });
  }

  Sentry.addBreadcrumb({
    category: 'api.error',
    message: `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${statusCode}`,
    level: planGated ? 'warning' : 'error',
    data: { statusCode, duration },
  });
}

function handleNetworkError(error: AxiosError, duration: number) {
  console.error('Network Error:', error.message);

  Sentry.withScope((scope: any) => {
    scope.setContext('network_error', {
      url: error.config?.url,
      method: error.config?.method,
      message: error.message,
      duration,
    });
    scope.setLevel('warning');
    scope.setTag('error_type', 'network');
    Sentry.captureException(sanitizeAxiosErrorForCapture(error));
  });

  Sentry.addBreadcrumb({
    category: 'api.network_error',
    message: `Network error: ${error.message}`,
    level: 'error',
  });
}

api.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = Date.now() - (response.config as any).metadata?.startTime;

    // Add breadcrumb for successful response
    Sentry.addBreadcrumb({
      category: 'api.response',
      message: `${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      level: 'info',
      data: {
        status: response.status,
        duration,
      },
    });

    // Log slow requests
    if (duration > 3000) {
      console.warn(`Slow API request: ${response.config.url} took ${duration}ms`);
      Sentry.addBreadcrumb({
        category: 'performance',
        message: `Slow API request: ${response.config.url}`,
        level: 'warning',
        data: {
          url: response.config.url,
          duration,
        },
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    const duration = Date.now() - ((error.config as any)?.metadata?.startTime || Date.now());
    const originalRequest = error.config as any;

    if (error.response) {
      const retryResult = await handleTokenRefreshOnUnauthorized(error, originalRequest, duration);
      if (retryResult) return retryResult;

      const errorCode = (error.response.data as any)?.errorCode;
      if (error.response.status === 403 && errorCode === 'NOT_ORG_MEMBER') {
        onOrgAccessDenied?.();
      }

      handleResponseError(error, originalRequest, duration);
    } else if (error.request) {
      handleNetworkError(error, duration);
    }

    throw error;
  },
);

interface TrafficSourcesParams {
  organizationId: string;
  startDate: string;
  endDate: string;
}

interface GeoDistributionParams {
  organizationId: string;
  startDate: string;
  endDate: string;
  limit?: number;
}

interface LeadsOverTimeParams {
  organizationId: string;
  startDate: string;
  endDate: string;
  granularity?: 'day' | 'week' | 'month';
}

interface DashboardSummaryParams {
  organizationId: string;
  // Mirrors `DashboardSummaryQueryDto.period`. A value outside this set is
  // answered with the 30-day default, so the type must not be wider than the
  // enum the API validates against.
  period: '7d' | '30d' | '90d';
}

export const analyticsAPI = {
  getTrafficSources: (params: TrafficSourcesParams) =>
    api.get('/analytics/traffic-sources', { params }),

  getGeoDistribution: (params: GeoDistributionParams) =>
    api.get('/analytics/geo-distribution', { params }),

  getLeadsOverTime: (params: LeadsOverTimeParams) =>
    api.get('/analytics/leads-over-time', { params }),

  getDashboardSummary: (params: DashboardSummaryParams) =>
    api.get('/analytics/dashboard-summary', { params }),

  getPipelineVelocity: (params: { organizationId: string; period: string }) =>
    api.get('/analytics/pipeline/velocity', { params }),

  getTimeInStage: (params: {
    organizationId: string;
    templateId: string;
    startDate: string;
    endDate: string;
  }) => api.get('/analytics/pipeline/time-in-stage', { params }),

  getCycleTime: (params: { organizationId: string; startDate: string; endDate: string }) =>
    api.get('/analytics/pipeline/cycle-time', { params }),

  getStageDropoff: (params: {
    organizationId: string;
    templateId: string;
    startDate: string;
    endDate: string;
  }) => api.get('/analytics/pipeline/stage-dropoff', { params }),

  getSourcePerformance: (params: { organizationId: string; startDate: string; endDate: string }) =>
    api.get('/analytics/campaign/source-performance', { params }),

  getDeviceBreakdown: (params: { organizationId: string; startDate: string; endDate: string }) =>
    api.get('/analytics/campaign/device-breakdown', { params }),

  getGeoPerformance: (params: { organizationId: string; startDate: string; endDate: string }) =>
    api.get('/analytics/campaign/geo-performance', { params }),

  getAgentPerformance: (params: {
    organizationId: string;
    startDate: string;
    endDate: string;
    userId?: string;
  }) => api.get('/analytics/agents/performance', { params }),

  // No date range: untouched-ness is a state, not a movement inside a window.
  getUntouchedLeads: (params: { organizationId: string }) =>
    api.get('/analytics/agents/untouched-leads', { params }),

  getResponseTime: (params: { organizationId: string; startDate: string; endDate: string }) =>
    api.get('/analytics/agents/response-time', { params }),

  getMRRTrend: (params: { organizationId: string; months?: number }) =>
    api.get('/analytics/revenue/mrr-trend', { params }),
};

// ---------------------------------------------------------------------------
// Public geo API — no auth token required ('/geo' is in the public list above).
// Returns the buyer's detected country/currency for display only (no FX).
// ---------------------------------------------------------------------------

export interface GeoResponse {
  country: string | null;
  currency: string;
  source: 'ip' | 'fallback';
}

export const geoAPI = {
  /**
   * GET /geo — public IP-based country/currency detection.
   * Sends no Authorization header.
   */
  get: () => api.get<GeoResponse>('/geo'),
};

export const dashboardAPI = {
  getOverview: (params: { organizationId: string; startDate: string; endDate: string }) =>
    api.get('/dashboard/overview', { params }),
};

export const authAPI = {
  getCurrentUser: () => api.get('/auth/me'),

  getMetadata: () => api.get('/auth/metadata'),

  updateMetadata: (metadata: any) => api.patch('/auth/metadata', metadata),
};

export interface WaitlistData {
  email: string;
  fullName?: string;
  company?: string;
  phone?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPage?: string;
}

export const waitlistAPI = {
  join: (data: WaitlistData) => api.post('/waitlist', data),
};

export interface SignupAttributionPayload {
  email: string;
  attribution: Record<string, unknown>;
  anonDeviceId?: string;
}

/**
 * Hands the campaign the visitor arrived from to the server before they leave
 * for their inbox. The login code arrives by email, so a visitor who started in
 * an in-app browser often finishes in another one — where none of this survives.
 */
export const signupAttributionAPI = {
  capture: (data: SignupAttributionPayload) => api.post('/auth/attribution', data),
};

export interface LeadsQueryParams {
  organizationId?: string;
  stageId?: string;
  assignedToId?: string;
  source?: string;
  search?: string;
  jobTitle?: string;
  industry?: string;
  economicCapacity?: 'High' | 'Medium' | 'Low';
  location?: string;
  tags?: string[];
  qualificationStatus?: 'qualified' | 'not_qualified_title' | 'pending_validation';
  startDate?: string;
  endDate?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'fullName' | 'company';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  viewMode?: 'my' | 'team';
}

export const leadsAPI = {
  getAll: (params: LeadsQueryParams) => api.get('/leads', { params }),

  getOne: (id: string, organizationId: string) =>
    api.get(`/leads/${id}`, { params: { organizationId } }),

  create: async (data: {
    organizationId: string;
    funnelTemplateId: string;
    email?: string;
    phone?: string;
    company?: string;
    fullName?: string;
    source: string;
    sourceUrl?: string;
    jobTitle?: string;
    customFields?: Record<string, unknown>;
    tags?: string[];
    assignedTo?: string;
    primaryContactRole?: string;
  }) => api.post('/leads', data),

  update: (
    id: string,
    organizationId: string,
    data: {
      email?: string | null;
      phone?: string | null;
      company?: string | null;
      fullName?: string;
      jobTitle?: string | null;
      sourceUrl?: string;
      customFields?: Record<string, unknown>;
      tags?: string[];
      assignedTo?: string | null;
    },
  ) => api.patch(`/leads/${id}`, data, { params: { organizationId } }),

  delete: (id: string, organizationId: string) =>
    api.delete(`/leads/${id}`, { params: { organizationId } }),

  moveStage: (id: string, stageId: string, organizationId: string) =>
    api.patch(`/leads/${id}/stage`, { stageId }, { params: { organizationId } }),

  addNote: (id: string, content: string, organizationId: string) =>
    api.post(`/leads/${id}/notes`, { content }, { params: { organizationId } }),

  getActivities: (
    id: string,
    organizationId: string,
    limit = 25,
    cursor?: string,
    includeAutomation = false,
  ) =>
    api.get(`/leads/${id}/activities`, {
      params: { organizationId, limit, cursor, includeAutomation },
    }),

  getStats: (organizationId: string) =>
    api.get('/leads/stats/overview', { params: { organizationId } }),

  addContact: (
    leadId: string,
    organizationId: string,
    data: {
      name?: string;
      email?: string;
      phone?: string;
      jobTitle?: string;
      role?: string | null;
    },
  ) => api.post(`/leads/${leadId}/contacts`, data, { params: { organizationId } }),

  updateContact: (
    leadId: string,
    contactId: string,
    organizationId: string,
    data: {
      name?: string | null;
      email?: string | null;
      phone?: string | null;
      jobTitle?: string | null;
      role?: string | null;
    },
  ) =>
    api.patch(`/leads/${leadId}/contacts/${contactId}`, data, {
      params: { organizationId },
    }),

  removeContact: (leadId: string, contactId: string, organizationId: string) =>
    api.delete(`/leads/${leadId}/contacts/${contactId}`, {
      params: { organizationId },
    }),

  setPrimaryContact: (leadId: string, contactId: string, organizationId: string) =>
    api.patch(`/leads/${leadId}/contacts/${contactId}/primary`, {}, { params: { organizationId } }),

  mergeLeads: (targetLeadId: string, sourceLeadId: string, organizationId: string) =>
    api.post(`/leads/${targetLeadId}/merge`, { sourceLeadId }, { params: { organizationId } }),

  getDuplicates: (organizationId: string) =>
    api.get('/leads/duplicates', { params: { organizationId } }),

  setMarketingConsent: (leadId: string, organizationId: string, body: SetMarketingConsentBody) =>
    api.patch<{ id: string; marketingOptIn: boolean | null }>(
      `/leads/${leadId}/marketing-consent`,
      body,
      { params: { organizationId } },
    ),
};

export const consentAPI = {
  getStatus: (leadId: string, organizationId: string) =>
    api.get<ConsentStatus>(`/voice-compliance/consents/${leadId}/status`, {
      params: { organizationId },
    }),

  record: (organizationId: string, body: RecordConsentBody) =>
    api.post<{ recorded: true }>('/voice-compliance/consents', body, {
      params: { organizationId },
    }),
};

export const settingsAPI = {
  updateProfile: (data: { fullName: string }) => api.patch('/auth/profile', data),

  updateOrganization: (
    orgId: string,
    data: { name?: string; locale?: string; timezone?: string },
  ) => api.patch(`/organizations/${orgId}`, data),

  listMembers: (orgId: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/organizations/${orgId}/members`, { params }),

  listInvites: (orgId: string, params?: { status?: string }) =>
    api.get(`/organizations/${orgId}/invites`, { params }),

  createInvite: (orgId: string, data: { email: string; role?: string }) =>
    api.post(`/organizations/${orgId}/invites`, data),

  revokeInvite: (orgId: string, inviteId: string) =>
    api.delete(`/organizations/${orgId}/invites/${inviteId}`),

  updateMemberRole: (orgId: string, userId: string, role: string) =>
    api.patch(`/organizations/${orgId}/members/${userId}/role`, { role }),

  removeMember: (orgId: string, userId: string) =>
    api.delete(`/organizations/${orgId}/members/${userId}`),

  getOrgSettings: (orgId: string) => api.get(`/organizations/${orgId}/settings`),

  updateOrgSettings: (
    orgId: string,
    data: {
      boardVisibility?: string;
      jobTitleFilterEnabled?: boolean;
      workflowPause?: { workflowType: string; paused: boolean };
      businessHours?: {
        enabled: boolean;
        timezone: string | null;
        windows: { day: string; start: string; end: string }[];
        blackoutDates: string[];
      };
    },
  ) => api.patch(`/organizations/${orgId}/settings`, data),
};

export const subscriptionsAPI = {
  getSubscription: (organizationId: string) =>
    api.get('/subscriptions', { params: { organizationId } }),

  setPendingPlan: (organizationId: string, data: { plan: string; cycle: 'monthly' | 'yearly' }) =>
    api.patch('/subscriptions/pending-plan', data, { params: { organizationId } }),
};

export const pricingAPI = {
  /**
   * @param countryHint     locale-derived guess; the server only trusts it when
   *                        its own geo detection produced nothing.
   * @param selectedCountry the buyer's explicit choice (ISO code, or 'ZZ' for
   *                        the global market), which outranks detection.
   */
  getPublicPricing: (countryHint?: string | null, selectedCountry?: string | null) => {
    const params: Record<string, string> = {};

    const normalizedHint = normalizeCountryCode(countryHint);
    if (normalizedHint) {
      params.countryHint = normalizedHint;
    }

    const normalizedSelection = selectedCountry?.trim().toUpperCase();
    if (normalizedSelection) {
      params.country = normalizedSelection;
    }

    return Object.keys(params).length > 0 ? api.get('/pricing', { params }) : api.get('/pricing');
  },
};

export const billingAPI = {
  createCheckoutSession: (
    data: {
      organizationId: string;
      plan: string;
      cycle: 'monthly' | 'yearly';
      successUrl: string;
      cancelUrl: string;
    },
    idempotencyKey: string,
  ) =>
    api.post('/billing/checkout-session', data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  getCheckoutReadiness: (organizationId: string) =>
    api.get('/billing/checkout-readiness', { params: { organizationId } }),

  getCheckoutAttempt: (organizationId: string, attemptId: string) =>
    api.get(`/billing/checkout-attempts/${encodeURIComponent(attemptId)}`, {
      params: { organizationId },
    }),
};

export const jobTitleFiltersAPI = {
  getAll: (orgId: string) => api.get(`/organizations/${orgId}/job-title-filters`),

  create: (
    orgId: string,
    data: { title: string; country?: string; industry?: string; scoreBonus?: number },
  ) => api.post(`/organizations/${orgId}/job-title-filters`, data),

  update: (
    orgId: string,
    id: string,
    data: { isActive?: boolean; title?: string; scoreBonus?: number },
  ) => api.patch(`/organizations/${orgId}/job-title-filters/${id}`, data),

  delete: (orgId: string, id: string) =>
    api.delete(`/organizations/${orgId}/job-title-filters/${id}`),

  getSynonyms: (orgId: string) => api.get(`/organizations/${orgId}/job-title-filters/synonyms`),

  createSynonym: (orgId: string, data: { synonym: string; canonicalTitle: string }) =>
    api.post(`/organizations/${orgId}/job-title-filters/synonyms`, data),

  deleteSynonym: (orgId: string, id: string) =>
    api.delete(`/organizations/${orgId}/job-title-filters/synonyms/${id}`),
};

export const forceQualifyAPI = {
  forceQualify: (leadId: string, organizationId: string, reason: string) =>
    api.post(`/leads/${leadId}/force-qualify`, { reason }, { params: { organizationId } }),
};

export const funnelsAPI = {
  getTemplates: (organizationId: string) =>
    api.get('/funnels/templates', { params: { organizationId } }),

  getTemplate: (id: string) => api.get(`/funnels/templates/${id}`),

  createTemplate: (data: {
    organizationId: string;
    name: string;
    description?: string;
    stages: Array<{ name: string; color: string }>;
  }) => api.post('/funnels/templates', data),

  updateTemplate: (
    id: string,
    data: {
      name?: string;
      description?: string;
      isActive?: boolean;
    },
  ) => api.patch(`/funnels/templates/${id}`, data),

  getBoard: (templateId: string, organizationId: string, viewMode?: 'my' | 'team') =>
    api.get(`/funnels/templates/${templateId}/board`, {
      params: { organizationId, viewMode },
    }),
};

export interface NotificationItem {
  id: string;
  organizationId: string;
  userId: string;
  category: string;
  subType: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  state: string;
  channel: string;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationPreference {
  category: string;
  pushEnabled: boolean;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  emailDigest: 'immediate' | 'daily' | 'weekly';
}

export const notificationsAPI = {
  list: (params: {
    organizationId: string;
    category?: string;
    unreadOnly?: boolean;
    limit?: number;
    cursor?: string;
  }) =>
    api.get<{ notifications: NotificationItem[]; hasMore: boolean; nextCursor?: string }>(
      '/notifications',
      { params },
    ),

  getUnreadCount: (organizationId: string) =>
    api.get<{ count: number }>('/notifications/unread-count', {
      params: { organizationId },
    }),

  markAsRead: (id: string) => api.patch(`/notifications/${id}/read`),

  markAllAsRead: (organizationId: string) =>
    api.patch('/notifications/read-all', { organizationId }),

  getPreferences: (organizationId: string) =>
    api.get<NotificationPreference[]>('/notifications/preferences', {
      params: { organizationId },
    }),

  updatePreferences: (organizationId: string, preferences: NotificationPreference[]) =>
    api.put('/notifications/preferences', { organizationId, preferences }),

  registerDevice: (token: string, platform: string, deviceName?: string) =>
    api.post('/notifications/devices', { token, platform, deviceName }),

  unregisterDevice: (token: string) =>
    api.delete(`/notifications/devices/${encodeURIComponent(token)}`),
};

export interface TranslateRequest {
  texts: string[];
  targetLanguage: 'es' | 'pt';
  context?: string;
}

export interface TranslateResponse {
  translations: string[];
  cached: boolean[];
}

export const translationAPI = {
  translate: (organizationId: string, data: TranslateRequest) =>
    api.post<TranslateResponse>(`/translations/translate?organizationId=${organizationId}`, data),
};

export interface CreateSellerProfilePayload {
  userId: string;
  sellerType: SellerType;
  status?: SellerStatus;
  maxLeads?: number | null;
  bio?: string | null;
  phone?: string | null;
  industryIds?: string[];
}

export interface UpdateSellerProfilePayload {
  sellerType?: SellerType;
  status?: SellerStatus;
  maxLeads?: number | null;
  bio?: string | null;
  phone?: string | null;
  industryIds?: string[];
}

export const sellerProfilesAPI = {
  getAll: (orgId: string) => api.get<SellerProfile[]>(`/organizations/${orgId}/seller-profiles`),

  getOne: (orgId: string, userId: string) =>
    api.get<SellerProfile>(`/organizations/${orgId}/seller-profiles/${userId}`),

  create: (orgId: string, data: CreateSellerProfilePayload) =>
    api.post<SellerProfile>(`/organizations/${orgId}/seller-profiles`, data),

  update: (orgId: string, userId: string, data: UpdateSellerProfilePayload) =>
    api.patch<SellerProfile>(`/organizations/${orgId}/seller-profiles/${userId}`, data),

  remove: (orgId: string, userId: string) =>
    api.delete(`/organizations/${orgId}/seller-profiles/${userId}`),
};

export const industriesAPI = {
  getAll: (orgId: string) => api.get<Industry[]>(`/organizations/${orgId}/industries`),

  create: (orgId: string, data: { name: string }) =>
    api.post<Industry>(`/organizations/${orgId}/industries`, data),

  remove: (orgId: string, id: string) => api.delete(`/organizations/${orgId}/industries/${id}`),
};

export type AssignmentStrategy = 'round_robin' | 'load_based' | 'manual_only';

export interface LeadAssignmentRule {
  id: string;
  organizationId: string;
  strategy: AssignmentStrategy;
  isActive: boolean;
  assigneeUserIds: string[];
  maxLeadsPerUser: number | null;
  lastAssignedUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeadAssignmentPayload {
  organizationId: string;
  strategy: AssignmentStrategy;
  isActive?: boolean;
  assigneeUserIds: string[];
  maxLeadsPerUser?: number;
}

export interface UpdateLeadAssignmentPayload {
  strategy?: AssignmentStrategy;
  isActive?: boolean;
  assigneeUserIds?: string[];
  maxLeadsPerUser?: number | null;
}

export const leadAssignmentAPI = {
  getRule: (organizationId: string) =>
    api.get<LeadAssignmentRule | null>('/lead-assignment', { params: { organizationId } }),

  createRule: (data: CreateLeadAssignmentPayload) =>
    api.post<LeadAssignmentRule>('/lead-assignment', data),

  updateRule: (organizationId: string, data: UpdateLeadAssignmentPayload) =>
    api.patch<LeadAssignmentRule>('/lead-assignment', data, { params: { organizationId } }),

  deleteRule: (organizationId: string) =>
    api.delete('/lead-assignment', { params: { organizationId } }),
};

// ---------------------------------------------------------------------------
// Public changelog API — no auth token required (@Public() on both endpoints)
// ---------------------------------------------------------------------------

export type ChangelogStatus = 'DRAFT' | 'PUBLISHED';

export interface ChangelogEntry {
  id: string;
  title: string;
  slug: string;
  body: string;
  tags: string[];
  version?: string | null;
  status: ChangelogStatus;
  publishedAt?: string | null;
  authorId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChangelogFeedResponse {
  data: ChangelogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetChangelogFeedParams {
  limit?: number;
  offset?: number;
}

export const changelogAPI = {
  /**
   * GET /changelog — public feed (PUBLISHED only, newest first).
   * Sends no Authorization header.
   */
  getFeed: ({ limit = 20, offset = 0 }: GetChangelogFeedParams) =>
    api.get<ChangelogFeedResponse>('/changelog', { params: { limit, offset } }),

  /**
   * GET /changelog/:slug — single published entry by slug.
   * Sends no Authorization header.
   */
  getEntry: (slug: string) => api.get<ChangelogEntry>(`/changelog/${slug}`),
};

// ---------------------------------------------------------------------------
// Public blog API — no auth token required (channel = flowsell).
// Both endpoints are localized via a `locale` query param (en | es | pt).
// ---------------------------------------------------------------------------

export type BlogLocale = 'en' | 'es' | 'pt';

export interface BlogCategory {
  key: string;
  name: string;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  coverImageUrl?: string | null;
  publishedAt?: string | null;
  categories: BlogCategory[];
  body?: string;
  locale?: BlogLocale;
  availableLocales?: BlogLocale[];
}

export interface BlogFeedResponse {
  data: BlogPost[];
  total: number;
  page: number;
  limit: number;
}

export interface GetBlogPostsParams {
  locale?: BlogLocale;
  page?: number;
  limit?: number;
}

export const blogAPI = {
  /**
   * GET /blog/flowsell/posts — public, localized feed (newest first).
   * Sends no Authorization header.
   */
  getPosts: ({ locale = 'en', page = 1, limit = 20 }: GetBlogPostsParams) =>
    api.get<BlogFeedResponse>('/blog/flowsell/posts', {
      params: { locale, page, limit },
    }),

  /**
   * GET /blog/flowsell/posts/:slug — single localized post with markdown body.
   * Sends no Authorization header.
   */
  getPost: (slug: string, locale: BlogLocale = 'en') =>
    api.get<BlogPost>(`/blog/flowsell/posts/${slug}`, { params: { locale } }),
};

export default api;
